import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiHeart, FiHome, FiMessageCircle, FiWifi, FiCamera } = FiIcons;

const Accommodation = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const rooms = [
    {
      id: 'deluxe',
      name: 'Deluxe Double Room',
      price: '$18',
      priceLocal: 'LKR 6,000',
      capacity: '2 Guests',
      view: 'River View',
      features: ['Private Bath', 'River View', 'Free WiFi', 'Garden Access'],
      description: 'Perfect for couples seeking an intimate rainforest retreat',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: FiHeart,
      popular: false
    },
    {
      id: 'family',
      name: 'Family Room',
      price: '$25',
      priceLocal: 'LKR 8,000',
      capacity: '4-5 Guests',
      view: 'Garden View',
      features: ['2 Queen Beds', 'Family Bathroom', 'Garden View', 'Space for Children'],
      description: 'Spacious comfort for the whole family adventure',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: FiUsers,
      popular: true
    },
    {
      id: 'villa',
      name: 'The Whole Villa',
      price: '$105',
      priceLocal: 'LKR 32,000',
      capacity: '15 Guests',
      view: 'Exclusive Access',
      features: ['Private Kitchen', 'BBQ Area', 'Full Privacy', 'Group Activities'],
      description: 'Complete exclusivity for large groups and special occasions',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: FiHome,
      popular: false
    }
  ];

  const handleBookRoom = (roomId, roomName) => {
    const message = encodeURIComponent(`Hi! I'm interested in booking the ${roomName} at Villa Springwater. Can you check availability and confirm the rate?`);
    window.open(`https://wa.me/94704003478?text=${message}`, '_blank');
  };

  return (
    <section id="accommodation" className="relative py-20 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-5">
        <motion.div
          className="w-full h-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23059669' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`
          }}
          animate={{ x: [0, 100] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Choose Your
            <span className="gradient-primary bg-clip-text text-transparent ml-3">
              Perfect Stay
            </span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            From intimate couple retreats to exclusive group getaways - we have the perfect space for your Sinharaja adventure.
          </motion.p>
        </motion.div>

        {/* Price Anchor Notice */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="glass-dark rounded-2xl p-4 inline-block">
            <p className="text-emerald-400 font-semibold">
              💎 Resort Features at Hostel Prices - Book Direct & Save!
            </p>
          </div>
        </motion.div>

        {/* Rooms Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {rooms.map((room, index) => (
            <motion.div
              key={room.id}
              className={`relative group ${room.popular ? 'lg:scale-105' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 * index }}
            >
              {/* Popular Badge */}
              {room.popular && (
                <motion.div
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <div className="gradient-primary text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    Most Popular
                  </div>
                </motion.div>
              )}

              {/* Card */}
              <div className={`glass-morphism rounded-3xl overflow-hidden h-full transition-all duration-300 ${room.popular ? 'ring-2 ring-emerald-500/50' : ''} group-hover:scale-105 group-hover:shadow-2xl`}>
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Icon */}
                  <div className="absolute top-4 right-4">
                    <div className="glass-dark rounded-xl p-3">
                      <SafeIcon icon={room.icon} className="text-emerald-400 text-xl" />
                    </div>
                  </div>

                  {/* Capacity */}
                  <div className="absolute bottom-4 left-4">
                    <div className="glass-dark rounded-lg px-3 py-1">
                      <span className="text-white text-sm font-medium">{room.capacity}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Room Name */}
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-emerald-300 transition-colors">
                    {room.name}
                  </h3>

                  {/* View */}
                  <p className="text-emerald-400 font-medium mb-4">{room.view}</p>

                  {/* Description */}
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {room.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {room.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-3xl font-bold text-white">
                        {room.price}
                        <span className="text-lg text-gray-400 ml-1">/night</span>
                      </div>
                      <div className="text-sm text-gray-400">{room.priceLocal}</div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    onClick={() => handleBookRoom(room.id, room.name)}
                    className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all duration-300 ${
                      room.popular 
                        ? 'gradient-primary text-white shadow-lg hover:shadow-xl' 
                        : 'glass-dark text-white hover:bg-white/20'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={FiMessageCircle} />
                    Book This Room
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Info */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="glass-dark rounded-2xl p-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-4">
              <SafeIcon icon={FiWifi} className="text-emerald-400 text-xl" />
              <SafeIcon icon={FiCamera} className="text-emerald-400 text-xl" />
            </div>
            <p className="text-gray-300">
              All rooms include <span className="text-emerald-400 font-semibold">free WiFi</span>, 
              access to our <span className="text-emerald-400 font-semibold">natural spring pool</span>, 
              and <span className="text-emerald-400 font-semibold">river bathing area</span>. 
              Perfect for digital nomads and nature photographers!
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Accommodation;